import { watchImmediate } from "@vueuse/core";
import { computed, nextTick, ref, watch } from "vue";

/**
 * Drilldown frame-stack state machine. Owns the stack of frames, the
 * drill-in (`pushFrame`) and back (`popFrame`) operations, the slide-transition
 * direction, the initial-path seeding, and the host-items mirroring rule
 * (root frame under client-side filter, top frame under `no-filter`).
 *
 * Emits `update-current-item` whenever the active frame changes, and
 * `update:on-back` whenever the back-button availability changes (the
 * value is `popFrame` when a parent frame exists, otherwise `undefined`).
 *
 * @param {object} props  Owning component's reactive props; the composable
 *   reads `props.items`, `props.loadChildren`, `props.initialPath`.
 * @param {Function} emit Owning component's `defineEmits` emitter.
 * @param {{ noFilter: import('vue').Ref<boolean> }} options
 *   `noFilter` decides which frame mirrors `props.items` changes.
 */
export function useFrameStack(props, emit, { noFilter }) {
  let nextFrameKey = 0;
  function createFrame({ items, sourceItem }) {
    return { key: nextFrameKey++, items: items ?? [], sourceItem: sourceItem ?? null };
  }

  // Frame 0 mirrors props.items reactively; pushed frames hold static items
  // resolved via loadChildren. Initialised synchronously so the first render
  // already has the root frame.
  const stack = ref([createFrame({ items: props.items ?? [], sourceItem: null })]);
  const currentFrame = computed(() => stack.value.at(-1));
  const currentItems = computed(() => currentFrame.value?.items ?? []);
  const hasParent = computed(() => stack.value.length > 1);

  // True while seedFromInitialPath builds the deep stack; guards syncItemsFromProp.
  let seeding = false;

  // True when the live stack already represents `path`. Skips redundant re-seeds
  // when the host's initial-path matches the depth the user already drilled to.
  function stackMatchesInitialPath(path) {
    const sourceItems = stack.value.slice(1).map((frame) => frame.sourceItem);
    if (sourceItems.length !== path.length) {
      return false;
    }
    return path.every((item, index) => sourceItems[index]?.value === item?.value);
  }

  // Per-instance request counter. Stale loadChildren resolutions bail when the id no longer matches.
  let nextRequestId = 0;

  async function loadChildrenFor(item) {
    if (props.loadChildren) {
      return await props.loadChildren(item);
    }
    return item.children ?? [];
  }

  async function seedFromInitialPath(path) {
    if (!path?.length || stackMatchesInitialPath(path)) {
      return;
    }
    seeding = true;
    const requestId = ++nextRequestId;
    const frames = [createFrame({ items: props.items ?? [], sourceItem: null })];
    for (const item of path) {
      const children = await loadChildrenFor(item);
      if (requestId !== nextRequestId) {
        seeding = false;
        return;
      }
      frames.push(createFrame({ items: children, sourceItem: item }));
    }
    // One assignment for the whole stack avoids multiple slide transitions on open.
    stack.value = frames;
    emit("update-current-item", path.at(-1));
    await nextTick();
    seeding = false;
  }

  watchImmediate(() => props.initialPath, seedFromInitialPath);

  function syncItemsFromProp(newItems) {
    if (seeding) {
      return;
    }
    if (stack.value.length === 0) {
      return;
    }
    // Client-side filtering: only the root frame mirrors `items`; pushed frames come
    // from loadChildren. Under `no-filter` the host re-scopes `items` to the current
    // depth, so the visible top frame mirrors instead.
    const frame = noFilter.value ? stack.value.at(-1) : stack.value[0];
    frame.items = newItems ?? [];
  }

  watch(() => props.items, syncItemsFromProp);

  const direction = ref("push");
  const transitionName = computed(() => `slide-content-${direction.value}`);

  async function pushFrame(item) {
    if (!item) {
      return;
    }
    const requestId = ++nextRequestId;
    const children = await loadChildrenFor(item);
    if (requestId !== nextRequestId) {
      return;
    }
    direction.value = "push";
    stack.value.push(createFrame({ items: children, sourceItem: item }));
    emit("update-current-item", item);
  }

  function popFrame() {
    if (stack.value.length <= 1) {
      return;
    }
    nextRequestId++;
    direction.value = "pop";
    stack.value.pop();
    emit("update-current-item", currentFrame.value?.sourceItem ?? null);
  }

  function emitOnBack(value) {
    emit("update:on-back", value ? popFrame : undefined);
  }

  watchImmediate(hasParent, emitOnBack);

  return { currentFrame, currentItems, transitionName, pushFrame };
}
